import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';

import StatusErrorDialog from 'components/Dialogs/StatusErrorDialog';
import StaffingClickableAlert from 'components/Staffing/Projects/StaffingClickableAlert';

/**
 * Plain-text modal body (clipboard copy) for a project that still needs Re-sync Calendar.
 *
 * Set when schedule/meeting fields change after the project is already scheduled. The calendar
 * invite is stale until ops run Re-sync Calendar Event Info from the project ⋮ menu.
 *
 * @returns {string}
 */
export const buildNeedsResyncCalendarDetails = () =>
  [
    'Project details that belong on the Google Calendar invite were edited in Control Room, but the calendar event has not been updated yet.',
    '',
    'What this means:',
    'People on the invite may still see the old date, time, location, title, or meeting link.',
    '',
    'What to do:',
    'Open the project, then choose Re-sync Calendar Event Info from the overflow menu (three dots). That updates Calendar (and Zoom or Meet as needed) from this project and emails everyone on the invite. This notice disappears when resync succeeds.',
  ].join('\n');

const useStyles = makeStyles({
  section: {
    margin: '0 0 1em',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  heading: {
    display: 'block',
    fontWeight: 700,
  },
});

/**
 * Rich modal body: same copy as {@link buildNeedsResyncCalendarDetails}, with section labels bold.
 *
 * @param {object} props
 * @param {Record<string, string>} props.classes
 */
const NeedsResyncCalendarDetailsBody = ({ classes }) => (
  <>
    <p className={classes.section}>
      Project details that belong on the Google Calendar invite were edited in Control Room, but the
      calendar event has not been updated yet.
    </p>
    <p className={classes.section}>
      <strong className={classes.heading}>What this means:</strong>
      People on the invite may still see the old date, time, location, title, or meeting link.
    </p>
    <p className={classes.section}>
      <strong className={classes.heading}>What to do:</strong>
      Open the project, then choose Re-sync Calendar Event Info from the overflow menu (three dots).
      That updates Calendar (and Zoom or Meet as needed) from this project and emails everyone on
      the invite. This notice disappears when resync succeeds.
    </p>
  </>
);

NeedsResyncCalendarDetailsBody.propTypes = {
  classes: PropTypes.object.isRequired,
};

/**
 * Compact error Alert for `project.remindUserToRunResyncCalendar`. Opens a local
 * {@link StatusErrorDialog} so cards and detail do not share one global modal.
 *
 * @param {object} props
 * @param {string} [props.className]
 */
const NeedsResyncCalendarNotice = ({ className }) => {
  const classes = useStyles();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <StaffingClickableAlert
        severity="error"
        className={className}
        onClick={() => setIsDialogOpen(true)}
      >
        Needs calendar resync
      </StaffingClickableAlert>
      <StatusErrorDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title="Needs calendar resync"
        statusError={buildNeedsResyncCalendarDetails()}
      >
        <NeedsResyncCalendarDetailsBody classes={classes} />
      </StatusErrorDialog>
    </>
  );
};

NeedsResyncCalendarNotice.propTypes = {
  className: PropTypes.string,
};

export default NeedsResyncCalendarNotice;
